import { FileMetaData, FilesService } from "@/api";
import { toastAtom } from "@/atoms/toastAtom";
import { formatBytes, formatDateTime } from "@/utils/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useState } from "react";
import OneDriveFileSelectorModal from "../dialogs/OneDriveFileSelector";
import { useNavigate } from "@tanstack/react-router";

const CurrentFile : React.FC = () => {
  const [showOneDriveFileSelector, setShowOneDriveFileSelector] = useState(false);

  const toast = useAtomValue(toastAtom)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const selectedFile = useQuery({
    queryKey: ['selectedFile'],
    queryFn: () => FilesService.selectedFiles(),
    enabled: true,
    refetchOnWindowFocus: false
  })

  const selectedFileData = selectedFile.data?.files[0]

  //リロード
  const reloadMutation = useMutation({
    mutationFn: (fileId: string) => FilesService.downloadOnedriveFile({
      file_id: fileId
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selectedFile'] })
      toast?.show({
        severity: 'success',
        summary: 'ファイル選択',
        detail: `${selectedFileData?.original_name} が再読込されました`,
        life: 3000
      })
    },
    onError: () => {
      toast?.show({
        severity: 'error',
        summary: 'エラー',
        detail: 'ファイルの再読込に失敗しました',
        life: 3000
      })
    }
  })
  //選択解除
  const unselectMutation = useMutation({
    mutationFn: () => FilesService.unselect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selectedFile'] })
      toast?.show({
        severity: 'success',
        summary: 'ファイル選択解除',
        detail: `${selectedFileData?.original_name} が選択解除されました`,
        life: 3000
      })
    },
    onError: () => {
      toast?.show({
        severity: 'error',
        summary: 'エラー',
        detail: 'ファイルの選択解除に失敗しました',
        life: 3000
      })
    }
  })

  const isLoading = selectedFile.isLoading || reloadMutation.isPending || unselectMutation.isPending

  const handleReloadFile = (file: FileMetaData, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (file.onedrive_file_id) {
      reloadMutation.mutate(file.onedrive_file_id)
    } else {
      toast?.show({
        severity: 'error',
        summary: 'エラー',
        detail: 'アップロードされたファイルは再読込できません',
        life: 3000
      })
    }
  }
  const handleUnselectFile = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    await unselectMutation.mutate()
    await queryClient.clear() //実際には、'createPlan'のキャッシュのみを消したいのだが…
  }


  // for datatable
  const fileNameTemplate = (rowData: FileMetaData) => {
    return (
      <div
        className="flex align-items-center cursor-pointer"
      >
        <i className="pi pi-file-excel text-green-500 text-xl mr-2" />
        <span>{rowData.original_name}</span>
      </div>
    )
  }

  const actionsTemplate = (rowData: FileMetaData) => {
    return (
      <div className="flex justify-content-end">
        {rowData.onedrive_file_id && (<Button
          icon="pi pi-refresh"
          className="p-button-rounded p-button-text p-button-info"
          tooltip="OneDriveから再読み込み"
          tooltipOptions={{ position: 'top' }}
          onClick={e => handleReloadFile(rowData, e)}
        />) }
        <Button
          icon="pi pi-times-circle"
          className="p-button-rounded p-button-text p-button-warning"
          tooltip="選択を解除"
          tooltipOptions={{ position: 'left' }}
          onClick={handleUnselectFile}
        />
      </div>
    )
  }

  return (
    <>
    <OneDriveFileSelectorModal visible={showOneDriveFileSelector} onClose={()=>setShowOneDriveFileSelector(false)}/>
    <Card className="shadow-2 mb-4" title="選択中のファイル">
    {selectedFileData ? (
      <div className="flex flex-column justify-content-center">
      <DataTable
        value={[selectedFileData]}
        loading={isLoading}
        rowHover
        stripedRows
        responsiveLayout="scroll"
        size="small"
        emptyMessage="選択中のファイルはありません"
        rowClassName={() => 'cursor-pointer'}
      >
        <Column
          field="name"
          header="ファイル名"
          body={fileNameTemplate}
          className="w-6"
        />
        <Column
          field="size"
          header="サイズ"
          body={(rowData: FileMetaData) => formatBytes(rowData.size)}
        />
        <Column field="onedrive_file_id" header="保存場所"
        body={(rowData: FileMetaData) => {
          return rowData.onedrive_file_id ? "OneDrive" : "アップロード"
        }} />
        <Column
          field="updatedAt"
          header="更新日時"
          body={(rowData: FileMetaData) =>
            rowData.modified_date ? formatDateTime(rowData.modified_date) : "-"
          }
        />
        <Column body={actionsTemplate} style={{ width: '180px' }} />
      </DataTable>
        <div className="flex flex-row align-items-center justify-content-center gap-2 mt-3">
          <Button
            label="選択ファイルを読み込んで生産計画を作成"
            icon="pi pi-arrow-right"
            severity='success'
            onClick={() => {
               navigate({
                to: '/create-plan'
               })
            }}
          />
        </div>
      </div>
    ) : (
      <div className="p-4 pt-0 text-center flex flex-column align-items-center">
        <p>
          選択中のファイルはありません。OneDriveからファイルを選択してください。
        </p>
        <div className="flex flex-row gap-2 align-items-center mt-3">
          <Button
            label="ファイルを選択"
            icon="pi pi-cloud"
            onClick={() => setShowOneDriveFileSelector(true)}
            severity='success'
          />
        </div>
      </div>
    )}
    </Card>
  </>
  );
}

export default CurrentFile;
